import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

const bookingPerm = permissionTable([
  [
    'manage_booking_operations',
    'Create and process bookings, check-in/out, allocation, and stay amendments',
  ],
]);

export const bookingGuides = [
  buildGuide({
    id: 'booking-overview',
    title: 'Booking Management Overview',
    moduleId: 'booking',
    summary:
      'How Roomnexa handles the guest journey from reservation to checkout.',
    keywords: ['booking', 'reservation', 'overview', 'front office'],
    pathHint: 'Hotel → Booking menus',
    related: ['reservation', 'walk-in', 'check-in', 'check-out', 'booking-list'],
    overview: [
      ...paragraphs(
        'Booking Management is the heart of Roomnexa front office. Every guest stay — whether booked weeks ahead, walked in at night, or synced from an OTA — becomes a booking record that moves through allocation, check-in, in-house stay, billing, and check-out.',
        'Think of a booking as the digital folio for one stay: guest identity, dates, room type or room number, rate and meal plan, notes, documents, charges, and payments. Front-desk staff open different booking screens depending on the job: create (Reservation / Walk-in), arrive (Check-in), settle (Check-out), or find and fix (Booking List / Detail).',
        'Statuses tell you what is allowed next. A reserved booking can be allocated and checked in; a checked-in booking can take POS charges and later check out; cancelled and no-show bookings free inventory according to your property rules. Learning the status table below prevents most front-desk mistakes.',
        'This overview is for first-time hotel staff. Use it to pick the right screen, then open the related guides for step-by-step detail. On a busy night desk, the practical order is usually: search Booking List → create only if nothing exists → allocate a clean room → check in → settle POS and room at departure.'
      ),
      table(
        ['Status', 'Meaning', 'Typical next action'],
        [
          [
            'Pending / Reserved / Confirmed',
            'Booking held for future or current arrival',
            'Allocate room → Check-in',
          ],
          [
            'Checked In',
            'Guest in-house; room occupied',
            'POS extras, housekeeping, folio charges',
          ],
          [
            'Checked Out',
            'Stay completed; room released for HK',
            'Archive / reports; do not edit casually',
          ],
          [
            'Cancelled',
            'Booking cancelled before or without stay',
            'Review refund / advance policy',
          ],
          [
            'No Show',
            'Guest did not arrive by cut-off',
            'Apply no-show rules; free inventory',
          ],
        ]
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets(
        [
          'Confirm you are inside the correct hotel (header property name).',
          'Know tonight’s occupancy from Dashboard before promising rooms.',
          'Agree your property cut-off for late arrivals vs no-show with the duty manager.',
          'Have Housekeeping Room Status open when assigning keys.',
        ],
        'Checklist before you take a live booking'
      ),
      callout(
        'important',
        'Permissions',
        'If Booking menus are missing, ask your admin for manage_booking_operations — do not create paper bookings outside the system.'
      ),
    ],
    steps: [
      heading('Choose the right screen'),
      steps([
        {
          text: 'Open Reservation when a guest or agent books before arrival day.',
        },
        {
          text: 'Open Walk-in when a guest is already at the desk with no prior booking (common for night walk-ins).',
        },
        {
          text: 'Open Room Allocation to assign or change room numbers before or at arrival.',
        },
        {
          text: 'Open Check-in when the guest arrives and you are ready to mark them in-house.',
        },
        {
          text: 'During the stay, use Booking Detail for notes, extras, and payment links; use POS for restaurant/room service.',
        },
        {
          text: 'Open Check-out at departure to settle the folio (room + POS) and free the room.',
        },
        {
          text: 'Use No Show after your cut-off when expected arrivals never came.',
        },
        {
          text: 'Use Booking List whenever someone calls “I have a booking” — search before creating anything new.',
        },
      ]),
      bullets(
        [
          'Reservation — guest books in advance',
          'Walk-in — guest arrives without booking',
          'Check-in — arrival processing + room assignment',
          'Check-out — settle and release room',
          'Room Allocation — assign rooms before/at arrival',
          'No Show — mark missed arrivals',
          'Booking List / Detail — search and manage any booking',
          'Stay / Booking calendar — visual occupancy planning',
        ],
        'Booking menu map'
      ),
    ],
    screens: [
      bullets(
        [
          'Booking submenu items map 1:1 to the jobs above',
          'Dashboard calendar also creates Quick Bookings that become the same booking records',
          'Booking List rows open Booking Detail for folio and actions',
        ],
        'Where bookings live'
      ),
    ],
    workflow: [
      workflow('End-to-end stay', [
        'Create reservation / walk-in / OTA booking',
        'Allocate room',
        'Guest arrival → Check-in',
        'In-house (HK + POS)',
        'Billing & payment',
        'Check-out',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Availability is enforced when bookings are created or dates change. Roomnexa holds inventory for the selected room type and nights so you cannot oversell beyond configured sellable stock (subject to blocks, OOO, and channel sync).',
        'Check-in typically moves the room toward occupied; check-out frees it for housekeeping and resale. Housekeeping status (clean/dirty) is what tells front office whether a vacant room is safe to assign again.',
        'Payments and invoices attach to the booking folio. Outstanding balances should be cleared at checkout unless your property allows city-ledger or company exceptions with supervisor approval.',
        'Channel/OTA bookings that sync into Roomnexa are processed like any other arrival: allocate, check in, charge extras, check out. Do not recreate them as walk-ins.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Search before creating',
        'Always search Booking List by mobile or booking code before creating a duplicate reservation. Guests often call twice on the same day.'
      ),
      callout(
        'tip',
        'Night walk-in pattern',
        'For a 11 PM walk-in: check Dashboard availability → Walk-in with ID → assign a Clean vacant room → Check-in → quote total with taxes → take advance if policy requires.'
      ),
      callout(
        'note',
        'Train by guest journey',
        'New staff learn faster if you teach reserve → arrive → stay → pay → leave before deep configuration screens.'
      ),
    ],
    bestPractices: [
      bullets([
        'Capture mobile numbers accurately — they power search, WhatsApp, and payment links.',
        'Add special request notes at booking time (early CI, cot, high floor, late arrival ETA).',
        'Pre-allocate VIP and early arrivals each morning using Room Allocation + HK board.',
        'Never promise a specific room number until you see Clean / Vacant on Housekeeping.',
        'At shift handover, list arrivals, in-house issues, and unpaid folios in Booking List notes or your property log.',
        'Quote totals including taxes before confirming any reservation or walk-in.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating a second booking instead of editing dates on the existing one.',
        'Checking out without settling open POS / restaurant charges.',
        'Assigning dirty or OOO rooms because the guest is waiting.',
        'Working in the wrong hotel after switching properties.',
        'Marking No Show before the property cut-off when notes say “arriving late”.',
        'Skipping ID verification on walk-ins required for police/compliance reports.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No availability shown',
            'Sold out, inventory block, or OOO rooms',
            'Change dates/type; check Inventory Blocks and Housekeeping OOO',
          ],
          [
            'Cannot find booking',
            'Wrong filter, spelling, or hotel context',
            'Search Booking List by code, mobile, or widen dates; confirm hotel header',
          ],
          [
            'Guest says they booked on OTA',
            'Channel not synced yet or different name',
            'Search by OTA confirmation / mobile; wait for sync or escalate to distribution owner',
          ],
          [
            'Booking menus missing',
            'Role lacks manage_booking_operations',
            'Ask admin to grant permission',
          ],
          [
            'Duplicate stays on calendar',
            'Second booking created by mistake',
            'Cancel the duplicate with a reason; keep the original',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Do OTA bookings appear here?',
          a: 'Yes, when ChannelHub/Distribution syncs reservations into Roomnexa. Process them like any other arrival — do not recreate as walk-ins.',
        },
        {
          q: 'What is the difference between Reservation and Walk-in?',
          a: 'Reservation is for advance bookings. Walk-in is for guests already at the property without a prior booking, with stronger emphasis on same-day stay and ID capture.',
        },
        {
          q: 'Where do I see today’s arrivals?',
          a: 'Use Dashboard arrivals, Check-in list filtered to today, or Booking List filtered by arrival date and reserved status.',
        },
        {
          q: 'Can one booking cover multiple rooms?',
          a: 'Follow your property SOP. Many hotels create clear notes or separate bookings per room so folios and check-out stay clean.',
        },
        {
          q: 'Who can cancel or change rates?',
          a: 'Users with booking permissions can edit within policy; large discounts, refunds, and overrides should go through a supervisor account.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reservation',
    title: 'Creating Reservations',
    moduleId: 'booking',
    summary:
      'Book guests in advance with guest details, dates, room preferences, and confirmation.',
    keywords: [
      'reservation',
      'advance booking',
      'book ahead',
      'confirm booking',
    ],
    pathHint: 'Booking → Reservation',
    related: ['walk-in', 'room-allocation', 'check-in', 'booking-list'],
    videos: [
      video(
        '8E928XvC_GQ',
        'Make a reservation in 30 seconds',
        'Watch a fast reservation flow in Roomnexa from start to confirmation.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Use Reservation when a guest, company, or travel agent books before arrival. The screen captures guest identity, stay dates, room type preference, rate/meal plan, optional documents, and special requests so inventory is held for those nights.',
        'Reservations feed the arrivals list on check-in day. A well-filled reservation (correct mobile, ETA notes, preferences) makes morning allocation and evening arrivals much faster.',
        'Example: a guest calls Monday for Friday–Sunday Deluxe, EP, arriving after 9 PM. You create a Reservation with those dates, room type, mobile, and a note “ETA 21:30 — do not mark no-show early.” On Friday you allocate a clean Deluxe and check them in.',
        'Do not use Reservation for a guest already standing at the desk with no prior booking — use Walk-in instead. Do not create a new reservation to change dates on an existing one — use Edit Booking.',
        'After save, the booking appears on the calendar and in Booking List. You may allocate a room number immediately or wait until check-in day if your SOP prefers flexible assignment.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Room types and rate plans configured in Property Setup / Configuration',
        'Know arrival and departure dates (and number of nights)',
        'Guest mobile number ready — ask them to repeat it',
        'Search Booking List first so you do not duplicate an existing hold',
        'Know the quoted rate / meal plan before confirming',
      ]),
      callout(
        'note',
        'Availability first',
        'If the requested type is sold out, offer another type or dates before taking a tentative promise outside Roomnexa.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Search Booking List by mobile or name. If a matching future booking exists, open and edit it instead of creating another.',
        },
        { text: 'Open Reservation from the booking menu.' },
        {
          text: 'Enter check-in and check-out dates and review availability for the requested room type.',
        },
        {
          text: 'Choose room type and rate plan / meal plan when prompted (e.g. Deluxe EP vs CP).',
        },
        {
          text: 'Fill guest details: first/last name, mobile, gender, and address fields (country, state, city) as required by your form.',
        },
        {
          text: 'Add email if available — format is validated when provided. Leave blank rather than inventing an address.',
        },
        {
          text: 'Upload ID documents when property policy requires them for advance bookings; otherwise you can collect at check-in.',
        },
        {
          text: 'Add special requests/notes: late arrival ETA, airport pickup, extra bed, high floor, allergy notes for F&B.',
        },
        {
          text: 'Review the tariff summary (room + taxes as shown) with the guest, then click Save / Confirm.',
        },
        {
          text: 'Note the booking code. Share confirmation via SMS/WhatsApp/email if communications are enabled, and tell the guest to bring the same mobile number at arrival.',
        },
      ]),
      callout(
        'success',
        'After Save',
        'The booking appears on the calendar and in Booking List with a reservable status. Allocate a specific room now or closer to arrival via Room Allocation.'
      ),
    ],
    screens: [
      fieldTable([
        ['Check-in / Check-out', 'Yes', 'Stay dates (nightly or as configured)'],
        ['Room type', 'Yes', 'Inventory class to hold'],
        ['Guest name', 'Yes', 'Primary guest on the folio'],
        ['Mobile', 'Yes', 'Primary contact and search key'],
        ['Gender', 'Often yes', 'Guest profile completeness'],
        ['Country / State / City', 'Often yes', 'Address compliance fields'],
        ['Email', 'No', 'Confirmations and receipts'],
        ['Documents', 'Policy', 'ID proofs when required early'],
        ['Rate plan / meal plan', 'Depends', 'Pricing and inclusions'],
        ['Special requests', 'No', 'Ops notes for FO, HK, and F&B'],
      ]),
    ],
    workflow: [
      workflow('Reservation lifecycle', [
        'Inquiry & quote',
        'Create reservation',
        'Confirm with guest',
        'Allocate room',
        'Arrival → Check-in',
        'Stay',
        'Check-out',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Creating a reservation reduces sellable availability for the selected room type and dates according to inventory rules. Failed availability checks block the save so you do not oversell.',
        'Cancellation and amendment policies may affect refunds and reopening of inventory. Always change dates through Edit Booking rather than creating duplicates — duplicates double-hold inventory and confuse arrivals.',
        'Advances and payment links (when enabled) attach to the booking folio. Record what was promised in notes so night audit and cashiers see the same story.',
        'OTA-originated reservations that already exist in Booking List should be edited or processed as arrivals — not re-keyed as a second Reservation.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Late arrivals',
        'Put ETA in notes and avoid marking No Show until your property cut-off. A note like “flight lands 23:40” protects the guest and your reviews.'
      ),
      callout(
        'tip',
        'Corporate bookings',
        'Put company name and GST needs in notes; link payments and invoices later from Booking Detail / finance tools as per SOP.'
      ),
      callout(
        'note',
        'Read back the booking',
        'Before hanging up, read back dates, room type, and mobile. Most “wrong date” disputes start on the phone call.'
      ),
    ],
    bestPractices: [
      bullets([
        'Quote total including taxes before confirming.',
        'For multi-room stays, create clear notes or separate bookings per your property SOP.',
        'Collect or request advance for peak dates per policy; note the amount expected.',
        'Standardise name format (as on ID) to help police log and search.',
        'After save, spot-check the calendar block for the correct nights.',
        'If the guest might upgrade later, note preferences now so allocation is easier.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Swapping check-in and check-out dates.',
        'Selecting the wrong room type when the guest asked for a specific category.',
        'Creating a duplicate because you did not search by mobile first.',
        'Leaving mobile blank or mistyped — later search and WhatsApp fail.',
        'Promising a specific room number without checking HK status.',
        'Using Reservation for a same-day desk arrival that should be Walk-in.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot save',
            'Missing required fields',
            'Complete highlighted fields (often gender/address)',
          ],
          [
            'No rooms available',
            'Sold out or blocked inventory',
            'Change type/dates or release inventory blocks with authorised user',
          ],
          [
            'Email invalid',
            'Format validation failed',
            'Correct the email or leave blank',
          ],
          [
            'Wrong rate shown',
            'Incorrect rate plan selected',
            'Change plan before confirm; escalate rate overrides to supervisor',
          ],
          [
            'Guest already has a booking',
            'Duplicate create',
            'Cancel the new one; edit the original',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I assign a room number at reservation time?',
          a: 'Yes via Room Allocation, or leave unassigned until check-in day if your SOP prefers flexible assignment.',
        },
        {
          q: 'How do I change dates later?',
          a: 'Open the booking in Booking Detail → Edit Booking and re-check availability. Do not create a second reservation.',
        },
        {
          q: 'Can I take a tentative hold without confirming?',
          a: 'Follow property policy. If you save a reservation, inventory is held — release via cancel/edit if the guest does not confirm within your hold window.',
        },
        {
          q: 'What if the guest wants two different room types?',
          a: 'Create separate bookings or follow your multi-room SOP so each room’s folio and check-out stay clear.',
        },
        {
          q: 'Do I need ID at reservation time?',
          a: 'Only if your policy requires it. Many hotels collect full ID at check-in or walk-in; still capture accurate name and mobile now.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'walk-in',
    title: 'Creating Walk-in Bookings',
    moduleId: 'booking',
    summary:
      'Register guests who arrive without a prior reservation, including ID capture and same-day stay setup.',
    keywords: [
      'walk-in',
      'walkin',
      'same day',
      'arrival without booking',
      'id extract',
    ],
    pathHint: 'Booking → Walk-in',
    related: ['reservation', 'check-in', 'room-allocation', 'customer-detail'],
    videos: [
      video(
        'Mm4uB4EnuG0',
        'Fast & easy walk-in booking',
        'Create single or multi-room walk-in bookings quickly at the front desk.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Walk-in bookings are for guests who arrive at the property without a reservation. Roomnexa walks you through guest details, identification, room selection, and creating the stay quickly so you can hand keys without losing compliance fields.',
        'Night walk-ins are the classic case: a guest arrives at 10:30 PM asking for one night. You confirm a Clean vacant room on Housekeeping, create the Walk-in with ID, assign the room, check in, and collect advance or full payment per policy.',
        'Many properties check in immediately after walk-in creation because the guest is already at the desk. Treat Walk-in + Check-in as one continuous flow, but still verify ID data before saving.',
        'Walk-in also supports future check-in dates when the form allows it, but clear advance bookings are usually cleaner as Reservation. Use Customer Hub / customer-detail later if you want a lasting CRM profile beyond this stay.',
        'If ID extract / OCR is available, use it to speed typing — then verify every field against the physical ID. Misread names and ID numbers cause police-log and dispute problems.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Verify availability on Dashboard for tonight (and departure date)',
        'Open Housekeeping → Room Status and identify Clean vacant rooms',
        'Have ID documents ready if policy requires',
        'Know the rate to quote including taxes',
        'Search Booking List by mobile in case they already have a reservation or OTA booking',
      ]),
      callout(
        'warning',
        'Do not skip the search',
        'Guests sometimes walk in saying “we booked on the app.” Find the OTA/reservation first; creating a walk-in on top double-sells the stay.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Greet the guest and ask if they have any confirmation SMS, OTA voucher, or booking code. Search Booking List before creating.',
        },
        {
          text: 'Confirm a sellable room: correct type, Clean / Vacant on Room Status, not OOO.',
        },
        { text: 'Open Walk-in from the booking menu.' },
        {
          text: 'Enter guest name (first and last), mobile number, gender, and address fields (country, state, city as required).',
        },
        {
          text: 'Add email optionally — if entered, it must be a valid format.',
        },
        {
          text: 'Upload identification documents. If ID extract is available, use it to auto-fill fields, then verify every value with the guest and the physical ID.',
        },
        {
          text: 'Select room type, dates (often same-day check-in to tomorrow or later check-out), and rate plan. Quote the total aloud.',
        },
        {
          text: 'Review charges and save the walk-in booking. Note the booking code.',
        },
        {
          text: 'Assign a room via Room Allocation or during Check-in, then complete Check-in if the guest is taking the room now.',
        },
        {
          text: 'Collect advance/deposit or full payment per property policy; explain breakfast, Wi‑Fi, and checkout time.',
        },
      ]),
      callout(
        'warning',
        'Verify auto-extracted ID data',
        'OCR/extract can misread names or ID numbers. Always confirm with the physical ID before saving.'
      ),
    ],
    screens: [
      fieldTable([
        ['First / Last name', 'Yes', 'Primary guest identity'],
        ['Mobile', 'Yes', 'Contact + search key'],
        ['Country / State / City', 'Yes (typical)', 'Address compliance'],
        ['Gender', 'Yes (typical)', 'Profile field'],
        ['Email', 'No', 'Receipts / communication'],
        ['ID upload / extract', 'Policy', 'KYC / police log readiness'],
        ['Room type & dates', 'Yes', 'Stay inventory'],
        ['Rate plan', 'Depends', 'Pricing for the walk-in night(s)'],
      ]),
    ],
    workflow: [
      workflow('Walk-in same-day stay', [
        'Greet guest',
        'Search existing booking',
        'Check availability & HK board',
        'Create walk-in',
        'Assign clean room',
        'Check-in',
        'Collect payment / issue key',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Walk-in creates a booking like any other source. Inventory is reserved for the selected dates as soon as the booking saves successfully.',
        'If you check in immediately, room status becomes occupied and housekeeping will later mark the room dirty on checkout. Assigning a dirty room creates guest complaints — wait for Clean status.',
        'Guest fields captured here support Police Log and compliance reports. Incomplete address/gender/ID is a common reason those reports look wrong later.',
        'Customer Hub profiles (customer-detail) may be linked or created over time from guest data; still search Booking List first for operational stays.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Police / compliance',
        'Complete verified ID details so Police Log reports remain accurate. Prefer name spelling exactly as on the ID.'
      ),
      callout(
        'tip',
        'Busy night desk',
        'Pre-identify two Clean rooms of each popular type before peak walk-in hours so you are not searching the board while the guest waits.'
      ),
      callout(
        'note',
        'Walk-in vs Quick Booking',
        'Walk-in is the full guest + document flow. Quick Booking from the Dashboard calendar is faster when ID capture can wait — follow your SOP.'
      ),
    ],
    bestPractices: [
      bullets([
        'Quote total with taxes before taking payment.',
        'Collect advance/deposit per property policy at walk-in.',
        'Photograph/upload clear ID images — blurry uploads fail extract and audits.',
        'Tell HK immediately if the guest needs early room readiness next morning for a day-use or early departure pattern.',
        'Add vehicle number or companion count in notes if your property records them.',
        'After check-in, confirm the room number on the key card matches Roomnexa.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Skipping mobile number — makes later search and WhatsApp difficult.',
        'Assigning a dirty room before HK finishes.',
        'Trusting OCR without reading the ID aloud with the guest.',
        'Creating a walk-in when an OTA reservation already exists.',
        'Selecting wrong departure date (one-night guest left as multi-night).',
        'Forgetting to check in after creating the walk-in — guest has keys but status still reserved.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'ID extract failed',
            'Blurry photo or unsupported layout',
            'Enter details manually and upload clearer photos',
          ],
          [
            'Required field errors',
            'Missing address/gender fields',
            'Complete highlighted fields',
          ],
          [
            'No room types listed',
            'Property Setup incomplete',
            'Finish rooms/rates configuration with admin',
          ],
          [
            'No clean rooms',
            'All vacant rooms dirty or OOO',
            'Prioritise HK cleaning or offer wait / different type',
          ],
          [
            'Guest already booked',
            'Existing reservation/OTA',
            'Cancel the accidental walk-in; process the original',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Walk-in vs Quick Booking?',
          a: 'Walk-in is the full guest + document flow. Quick Booking is faster from the calendar when ID capture is not needed yet.',
        },
        {
          q: 'Can walk-ins be for future dates?',
          a: 'Yes if the form allows future check-in, but most walk-ins are same-day. Use Reservation for clear advance bookings.',
        },
        {
          q: 'Should I create a Customer Hub profile too?',
          a: 'Booking guest fields are enough to open the stay. Use Customer Hub / customer-detail when you want lasting CRM history or retention tools.',
        },
        {
          q: 'What if the guest has no ID?',
          a: 'Follow property and local compliance policy. Many hotels cannot check in without valid ID — escalate to the duty manager; do not invent data.',
        },
        {
          q: 'Can I extend a walk-in later?',
          a: 'Yes — Edit Booking to extend dates after checking availability, then inform the guest of any rate difference.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'check-in',
    title: 'Check-in Process',
    moduleId: 'booking',
    summary:
      'Process arrivals: find the booking, assign a clean room, and mark the guest in-house.',
    keywords: ['check-in', 'checkin', 'arrival', 'assign room', 'in-house'],
    pathHint: 'Booking → Check-in',
    related: [
      'room-allocation',
      'housekeeping-room-status',
      'walk-in',
      'check-out',
    ],
    overview: [
      ...paragraphs(
        'Check-in converts an arrival booking into an in-house stay. You verify guest identity, assign (or confirm) a room, and complete check-in so the room shows occupied and the guest can be charged for stay extras and POS.',
        'Arrivals come from reservations, walk-ins, or OTA sync. The Check-in screen is your arrivals worklist for the day — filter by date, name, or booking code so you never check in the wrong “Sharma”.',
        'Room readiness is non-negotiable. A reserved booking with a pre-allocated room that is still Dirty must wait for Housekeeping. Offer luggage hold and a realistic ready time rather than handing keys to an uncleaned room.',
        'Example morning flow: print or open today’s arrivals → pre-allocate Clean rooms for VIP/early CI → when guest arrives, verify ID against booking → confirm room → complete Check-in → explain breakfast and checkout time.',
        'After check-in, Booking Detail is the command centre for notes, extras, and payments. Restaurant/room service uses POS against the in-house room.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Booking must exist (reservation, walk-in, or OTA)',
        'Prefer a Clean / inspected room from Housekeeping → Room Status',
        'Guest ID available if not already on file',
        'Know early check-in policy and any extra charges',
        'Confirm booking is not already Checked In',
      ]),
      callout(
        'important',
        'Room readiness',
        'Do not check guests into dirty or out-of-order rooms. Coordinate with Housekeeping → Room Status first.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Check-in (or find the booking from Dashboard arrivals / Booking List).',
        },
        {
          text: 'Find the booking using date, guest name, mobile, or booking code filters. Open the correct row when names are similar.',
        },
        {
          text: 'Verify guest name, dates, room type, and documents against the ID presented at the desk.',
        },
        {
          text: 'Check Housekeeping Room Status for the intended room — must be Clean / Vacant (or acceptable per SOP for stay-over moves).',
        },
        {
          text: 'Select an available room of the correct type, or confirm a pre-allocation from Room Allocation.',
        },
        {
          text: 'Update guest details if something changed at the desk (mobile, email, address).',
        },
        {
          text: 'Complete Check-in. Confirm status becomes Checked In and the room is occupied.',
        },
        {
          text: 'Issue keys and explain Wi‑Fi, breakfast, restaurant hours, and checkout time per property SOP.',
        },
        {
          text: 'Note any early check-in charges or upgrades on the folio before the guest leaves the desk.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Arrivals list with filters (date, name, code)',
          'Room picker showing available rooms',
          'Guest / document summary',
          'Confirm check-in action',
        ],
        'Check-in screen areas'
      ),
      fieldTable([
        ['Arrival date filter', '—', 'Narrows today’s expected arrivals'],
        ['Guest / booking search', '—', 'Finds the correct booking quickly'],
        ['Room selection', 'Yes at CI', 'Physical room assigned for the stay'],
        ['Guest details', 'Verify', 'Must match ID and contact needs'],
        ['Check-in confirm', 'Yes', 'Moves booking to Checked In'],
      ]),
    ],
    workflow: [
      workflow('Arrival day', [
        'Guest arrives',
        'Find booking on Check-in',
        'Verify ID',
        'Confirm Clean room',
        'Assign / confirm room',
        'Complete check-in',
        'Upsell / welcome',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'On successful check-in, booking status becomes Checked In and the room is treated as occupied for operations and inventory.',
        'Early check-in may be allowed based on availability and property policy. Extra charges for early CI belong on the booking folio so checkout balances are correct.',
        'Pre-allocation assigns a room number while status may still be reserved; check-in is the status transition that marks the guest in-house.',
        'Room moves after check-in must keep folio, POS room charges, and HK status aligned — always inform housekeeping when you shift a guest.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Early check-in',
        'Check HK status first. If the room is not ready, offer luggage hold or a waiting area and set a clear ready time.'
      ),
      callout(
        'tip',
        'Similar names',
        'Match booking code or mobile, not only surname. Two “Patel” arrivals on the same day are a classic wrong-room risk.'
      ),
      callout(
        'note',
        'Pre-allocate mornings',
        'VIP and early arrivals go smoother when Room Allocation is done against the Clean board before lobby rush.'
      ),
    ],
    bestPractices: [
      bullets([
        'Pre-allocate rooms in the morning for VIP / early arrivals.',
        'Match ID name to booking name before handing keys.',
        'Collect missing documents at check-in if reservation skipped them.',
        'Upsell only after room assignment is confirmed — do not re-quote endlessly in lobby queues.',
        'Tell F&B if meal plan includes breakfast so morning service is smooth.',
        'For group arrivals, check in one booking at a time and label keys clearly.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Checking in the wrong booking when two guests share similar names.',
        'Skipping document verification required for compliance.',
        'Handing keys while room status is still Dirty.',
        'Forgetting early CI charges until checkout disputes.',
        'Checking in again on an already Checked In booking (creates confusion — open Booking Detail instead).',
        'Assigning a room type different from the booked type without an authorised upgrade/downgrade.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No rooms to assign',
            'No clean vacant rooms of that type',
            'Open Room Allocation / HK status; prioritise cleaning; consider authorised upgrade',
          ],
          [
            'Booking not listed',
            'Wrong date filter or hotel',
            'Widen date filter; search Booking List by code/mobile',
          ],
          [
            'Already checked in',
            'Prior staff completed CI',
            'Open Booking Detail — do not create a duplicate',
          ],
          [
            'Room not in picker',
            'Wrong type, occupied, dirty, or OOO',
            'Fix HK/maintenance status or choose another room',
          ],
          [
            'Guest claims OTA booking missing',
            'Sync delay or name mismatch',
            'Search alternate fields; escalate distribution owner',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I change rooms after check-in?',
          a: 'Yes — use room shift/reallocation flows from allocation or booking edit per your SOP, then inform HK and update any open POS room charges.',
        },
        {
          q: 'What about early check-in charges?',
          a: 'Add extra charges on the booking folio if your property bills for early CI, and tell the guest before they leave the desk.',
        },
        {
          q: 'Do walk-ins use this screen too?',
          a: 'Yes. After Walk-in save, proceed to Check-in (or combined desk flow) so status becomes Checked In.',
        },
        {
          q: 'Is room allocation mandatory before check-in?',
          a: 'You can allocate during check-in. Pre-allocation simply speeds arrivals.',
        },
        {
          q: 'Guest arrives without the booking name on ID?',
          a: 'Follow property policy for accompanying guests and primary folio holder. Do not check in without authorised identity verification.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'check-out',
    title: 'Check-out Process',
    moduleId: 'booking',
    summary:
      'Settle the guest folio, collect balance payment, complete checkout, and free the room.',
    keywords: ['check-out', 'checkout', 'settlement', 'balance', 'departure'],
    pathHint: 'Booking → Check-out',
    related: [
      'payments',
      'pos-orders',
      'housekeeping-cleanings',
      'draft-invoice',
    ],
    overview: [
      ...paragraphs(
        'Check-out ends the in-house stay. You review room charges and POS/restaurant charges, collect any outstanding balance, generate invoices/receipts, and release the room for housekeeping.',
        'The most common checkout mistake is closing the room folio while a breakfast or room-service bill is still open in POS. Always ask: “Any restaurant charges this morning?” and check POS Orders / room bills before completing checkout.',
        'Example: Guest in Room 204 requests checkout at 11 AM. Folio shows room rent + taxes. POS shows last night’s dinner charged to room and this morning’s coffee still unpaid. Settle POS, collect remaining balance, print invoice (or use draft-invoice tools as configured), complete Check-out, then notify HK that 204 is dirty for turnover.',
        'Late checkout is a policy decision: approve time, post fee if required, then checkout when the guest actually leaves — do not checkout early just to clear the list.',
        'After checkout, the booking status is Checked Out. Corrections become sensitive; escalate financial fixes to a supervisor rather than creating a new stay.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Guest is Checked In',
        'Know if late checkout was approved and whether a fee applies',
        'Cashier / payment methods ready (cash, card, UPI, etc.)',
        'Check POS for open room orders before quoting the balance',
        'Keys / access cards ready to collect (physical SOP)',
      ]),
      callout(
        'warning',
        'Open restaurant bills',
        'Settle or transfer POS orders before checkout so revenue and guest balances stay correct.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Check-out and find the in-house guest by room number, name, or departure date.',
        },
        {
          text: 'Review the folio: room rent, taxes, extras, discounts, advances already paid.',
        },
        {
          text: 'Check POS Orders for that room/booking. Settle unpaid restaurant or room-service bills (or confirm room charge already posted).',
        },
        {
          text: 'Confirm any late checkout fees or allowances with a supervisor if needed; post charges to the folio.',
        },
        {
          text: 'Tell the guest the balance due. Collect outstanding amount using available payment methods (see Payments).',
        },
        {
          text: 'Generate invoice/receipt as required — use booking invoice actions or Draft Invoice tools depending on your setup.',
        },
        {
          text: 'Collect keys/cards and complete Check-out in Roomnexa.',
        },
        {
          text: 'Confirm booking status is Checked Out and the room is free for housekeeping.',
        },
        {
          text: 'Notify housekeeping (Room Cleanings / status board) that the room needs cleaning for possible same-day resell.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Departures list / filters',
          'Folio / charges summary',
          'Payment capture',
          'Invoice / PDF / draft invoice actions',
          'Confirm checkout',
        ],
        'Check-out screen areas'
      ),
      fieldTable([
        ['Guest / room search', '—', 'Finds the departure quickly'],
        ['Folio balance', 'Review', 'Amount still due after advances'],
        ['Payment methods', 'If balance > 0', 'Clears outstanding tender'],
        ['Invoice / receipt', 'Policy', 'Guest billing documents'],
        ['Check-out confirm', 'Yes', 'Ends in-house status'],
      ]),
    ],
    workflow: [
      workflow('Departure with POS bill', [
        'Guest requests checkout',
        'Review room folio',
        'Settle open POS orders',
        'Collect remaining balance',
        'Issue invoice / receipt',
        'Complete checkout',
        'HK cleans room',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Checkout sets booking status to Checked Out and frees inventory for resale after housekeeping processes the room. The room typically becomes dirty until a cleaning job is completed.',
        'Payment validation ensures recorded tenders match the balance you intend to close. Partial payments and multiple tenders may be allowed depending on finance setup — still aim for a clear zero balance at departure.',
        'Room-charge POS orders attach restaurant spend to the stay. Leaving them open understates revenue and creates guest disputes when they return to pay later.',
        'Refunds, if any, follow finance/payment flows and permissions. Do not “fix” a wrong checkout by creating a duplicate walk-in.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Express checkout',
        'Pre-review folios in the morning for scheduled departures to reduce lobby queues. Flag rooms with open POS early.'
      ),
      callout(
        'tip',
        'Same-day resell',
        'After checkout, tell HK which rooms are already rebooked tonight so cleaning priority matches arrivals.'
      ),
      callout(
        'note',
        'Invoices',
        'If the guest needs a formal invoice (GST details), use your invoice / draft-invoice process before they leave the desk.'
      ),
    ],
    bestPractices: [
      bullets([
        'Ask “Any restaurant charges this morning?” before closing.',
        'Update HK immediately for same-day resell rooms.',
        'Reconcile cash/card collections with Cash Counter / payments reports at shift end.',
        'Note mini-bar or damage charges before the guest leaves, with supervisor approval when needed.',
        'For company billings, confirm billing instructions on the booking before taking personal payment.',
        'Thank the guest and invite a review only after settlement is clean — unresolved balances create bad reviews.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Checking out with unpaid mini-bar/POS.',
        'Forgetting to collect keys / access cards (physical SOP).',
        'Checking out before late checkout time without posting the approved fee.',
        'Printing an incomplete invoice missing restaurant lines.',
        'Assuming zero balance because advance equals room rent while POS is still open.',
        'Reopening issues by creating a new booking instead of using authorised corrections.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Balance not zero',
            'Missing payment or open charges',
            'Post payment or adjust charges with authorised user; settle POS',
          ],
          [
            'Missing invoice',
            'Invoice action not used / setup incomplete',
            'Use invoice/PDF actions on booking or Draft Invoice tools',
          ],
          [
            'Room still occupied on calendar',
            'Checkout not completed or view stale',
            'Confirm checkout saved; refresh Dashboard',
          ],
          [
            'Guest disputes POS amount',
            'Wrong room charge or merge',
            'Open POS Orders + audit; correct with supervisor before closing',
          ],
          [
            'Payment failed',
            'Gateway / network / tender issue',
            'Retry or alternate method; do not checkout unpaid without approval',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'What is late checkout?',
          a: 'Guest stays past standard checkout time. Approve per policy, charge if required, then checkout when they leave.',
        },
        {
          q: 'Can I reopen a checked-out booking?',
          a: 'Corrections are sensitive — use authorised edit/finance flows or supervisor support; do not create duplicate stays casually.',
        },
        {
          q: 'Where do restaurant charges show?',
          a: 'In POS Orders and on the booking folio when charged to room. Settle them before checkout.',
        },
        {
          q: 'What if the company pays later?',
          a: 'Follow city-ledger / company billing SOP with supervisor approval. Document billing instructions on the booking.',
        },
        {
          q: 'Do I create a cleaning job manually?',
          a: 'After checkout, use Housekeeping → Room Cleanings / Room Status per your property process so the room returns to Clean for resale.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'room-allocation',
    title: 'Room Allocation',
    moduleId: 'booking',
    summary:
      'Assign or reassign specific room numbers to bookings before or at arrival.',
    keywords: [
      'room allocation',
      'assign room',
      'reallocate',
      'room number',
    ],
    pathHint: 'Booking → Room Allocation',
    related: ['check-in', 'housekeeping-room-status', 'reservation'],
    overview: [
      ...paragraphs(
        'Room Allocation connects a booking to a physical room number. Use it for morning pre-assignments, upgrades, connecting-room setups, and moves when a room becomes unavailable (maintenance, guest preference, or overbooking recovery).',
        'Allocation is not the same as check-in. A reserved booking can hold Room 305 while status remains reserved until the guest arrives and you complete Check-in.',
        'Example: At 8 AM you open today’s arrivals, open Housekeeping Room Status, allocate Clean rooms to each booking (VIP to quiet floors), and flag notes. At 2 PM arrivals, check-in is mostly “confirm and go.”',
        'When reallocating an in-house guest, coordinate with housekeeping and explain the move to the guest. POS room-service charges should follow the correct room context after the move.',
        'Always respect room type: assigning a lower type without agreement is a downgrade dispute; assigning a higher type may need an authorised upgrade rate change.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Know which rooms are Clean / Vacant on Housekeeping → Room Status',
        'Booking exists without a room, or needs a change',
        'Understand booked room type vs available upgrade options',
        'For in-house moves, guest agreement and HK readiness',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Room Allocation.' },
        {
          text: 'Select a booking that needs a room (unassigned arrivals or change requests).',
        },
        {
          text: 'Cross-check Housekeeping Room Status for Clean rooms matching the booked type.',
        },
        {
          text: 'View available rooms and pick one that matches preferences in notes (high floor, away from lift, accessible).',
        },
        { text: 'Assign the room and confirm.' },
        {
          text: 'If reallocating an in-house guest, choose the new room, confirm the move with the guest, and notify housekeeping.',
        },
        {
          text: 'For upgrades/downgrades, ensure rate/charge changes are authorised and noted on the folio.',
        },
        {
          text: 'Spot-check Booking Detail / calendar so the room number displays correctly before the guest arrives.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Unassigned bookings list',
          'Available rooms by type',
          'Assign / reassign actions',
          'Booking context (dates, type, notes)',
        ],
        'Allocation screen areas'
      ),
      fieldTable([
        ['Booking selector', 'Yes', 'Which stay receives the room'],
        ['Available rooms', 'Yes', 'Sellable rooms for the type/dates'],
        ['Assign action', 'Yes', 'Writes room number onto the booking'],
        ['Reassign action', 'When needed', 'Moves booking to another room'],
      ]),
    ],
    workflow: [
      workflow('Pre-arrival allocation', [
        'Review arrivals',
        'Check HK board',
        'Allocate rooms',
        'Flag VIP preferences',
        'Check-in faster at desk',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Allocation does not always mean checked-in. A booking can hold a room number while status remains reserved until check-in completes.',
        'The room must be free of conflicting occupancy for the stay dates. Overlaps, OOO, and dirty statuses should block or warn against unsafe assignment.',
        'Reallocation changes the physical room mapping used by front office, HK, and often room-service targeting — keep all teams aligned.',
        'Inventory for the room type was already held at booking time; allocation picks which physical unit fulfils that hold.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Upgrades',
        'When upgrading, ensure rate/charge changes are authorised and noted on the folio before the guest hears a new total at checkout.'
      ),
      callout(
        'tip',
        'Connecting rooms',
        'Allocate connecting or accessible rooms intentionally in the morning — last-minute scrambling causes wrong-room handovers.',
      ),
      callout(
        'note',
        'Hold for VIP',
        'If you pre-allocate a VIP room, note it so another agent does not reassign it during peak.'
      ),
    ],
    bestPractices: [
      bullets([
        'Allocate connecting rooms / accessible rooms intentionally — not last minute.',
        'Run a joint FO + HK board walk after morning cleanings.',
        'Keep a buffer clean room for walk-ins when occupancy allows.',
        'Never allocate OOO or maintenance rooms “temporarily.”',
        'Document preference fulfilment in booking notes (view, floor, twin beds).',
        'After group allocation, verify no two bookings share the same room number.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Assigning OOO or maintenance rooms.',
        'Moving guests without HK notification.',
        'Allocating dirty rooms to reduce lobby wait.',
        'Upgrading without authorising the rate difference.',
        'Reusing a room number still occupied until later checkout.',
        'Ignoring special-request notes during allocation.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No rooms listed',
            'Type mismatch, HK status, or overlaps',
            'Check type match, HK status, overlapping bookings',
          ],
          [
            'Wrong room assigned',
            'Clicked incorrect row',
            'Reallocate immediately; inform guest if keys already issued',
          ],
          [
            'Room disappears after refresh',
            'Taken by another booking or status change',
            'Re-check availability and HK board; assign alternate',
          ],
          [
            'Cannot move in-house guest',
            'Target room not ready / permission',
            'Wait for Clean; ask supervisor; complete HK first',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is allocation mandatory before check-in?',
          a: 'You can allocate during check-in; pre-allocation simply speeds arrivals.',
        },
        {
          q: 'Can I allocate before the room is cleaned?',
          a: 'You may plan a room number, but do not hand keys until Room Status is Clean. Prefer allocating only Clean rooms.',
        },
        {
          q: 'What if the allocated room goes OOO?',
          a: 'Open maintenance, reallocate the booking to another Clean room, and inform the guest if they already know the old number.',
        },
        {
          q: 'Does allocation change the rate?',
          a: 'Not by itself. Same-type moves keep rate; upgrades/downgrades need authorised folio changes.',
        },
        {
          q: 'Where do I see who is unassigned?',
          a: 'Room Allocation lists bookings needing rooms; Booking List and arrivals views also show missing room numbers.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'no-show',
    title: 'No Show Management',
    moduleId: 'booking',
    summary:
      'Handle guests who do not arrive; apply charges and release inventory per policy.',
    keywords: ['no show', 'noshow', 'did not arrive', 'cancellation'],
    pathHint: 'Booking → No Show',
    related: ['reservation', 'booking-list', 'payments'],
    overview: [
      ...paragraphs(
        'No Show is for reservations where the guest never arrived by your property cut-off. Marking no-show updates booking status, can apply charges based on policy, and returns inventory to sale when appropriate.',
        'It is different from cancellation: cancel is usually a guest or agent request before arrival; no-show is failure to arrive without timely cancellation. Financial treatment (guarantees, advances, first-night charge) depends on rate rules and property policy.',
        'Example night-audit style: at 11 PM review expected arrivals still reserved, call pending mobiles, honour “late flight” notes, then mark true no-shows, post charges if required, and free rooms for late walk-ins.',
        'Marking no-show too early creates angry guests and bad reviews when they arrive at midnight. Marking too late keeps inventory blocked while walk-ins are turned away.',
        'OTA no-shows may also need channel-policy alignment — coordinate with whoever owns Distribution so PMS and OTA statuses do not fight each other.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      callout(
        'note',
        'Wait for cut-off',
        'Call the guest or wait until your property’s no-show cut-off before releasing a prepaid VIP.'
      ),
      bullets([
        'Know tonight’s cut-off time from the duty manager',
        'Check booking notes for ETA / late arrival',
        'Know guarantee / advance / no-show charge policy',
        'Confirm the guest did not already check in under a misspelled name',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open No Show (or identify the booking from arrivals / Booking List still reserved after cut-off).',
        },
        {
          text: 'Search for late-arrival notes, payment advances, and any WhatsApp/SMS replies.',
        },
        {
          text: 'Call or message the guest once when policy requires — document the attempt in notes.',
        },
        {
          text: 'Confirm the guest did not arrive and no authorised late hold remains.',
        },
        { text: 'Mark as No Show.' },
        {
          text: 'Apply no-show charges if your policy and rate rules require them; record payment/advance handling via Payments as needed.',
        },
        {
          text: 'Verify inventory for those dates is available again for sale (and channels if applicable).',
        },
        {
          text: 'If a late walk-in needs the room, proceed with Walk-in only after inventory is free.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'No-show list / candidates',
          'Booking detail context',
          'Charge / status actions',
          'Notes for dispute history',
        ],
        'No-show screen areas'
      ),
      fieldTable([
        ['Booking reference', 'Yes', 'Which reservation is marked no-show'],
        ['Status action', 'Yes', 'Sets No Show status'],
        ['Charges', 'Policy', 'No-show fee / first night per rules'],
        ['Notes', 'Recommended', 'Call attempts, ETA, dispute context'],
      ]),
    ],
    workflow: [
      workflow('No-show night audit style', [
        'Review expected arrivals',
        'Call pending guests',
        'Honour late ETA notes',
        'Mark no-shows',
        'Post charges',
        'Resell rooms if allowed',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'No-show status distinguishes missed arrivals from cancellations for reporting and audit. Finance and occupancy reports treat them differently.',
        'Financial treatment depends on guarantees, advances, and property policy. Some bookings forfeit advance; others charge first night; prepaid OTAs follow channel rules.',
        'Inventory release after no-show makes those rooms sellable again for walk-ins or new reservations. If the room stays blocked, status or channel sync did not complete cleanly.',
        'Reversing a no-show when a guest arrives very late may require supervisor support — document heavily to protect night audit.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Protect reviews',
        'A polite call before marking no-show often converts to late arrival and saves the room sale.'
      ),
      callout(
        'tip',
        'ETA notes',
        'Train reservation agents to write ETAs. Night staff should trust those notes until cut-off.'
      ),
      callout(
        'warning',
        'Prepaid VIP',
        'Do not casually release prepaid or VIP guaranteed rooms — escalate before no-show + resell.'
      ),
    ],
    bestPractices: [
      bullets([
        'Document reason/notes for disputes.',
        'Align OTA no-show rules with channel policies.',
        'Keep a written cut-off time at the desk for every shift.',
        'Reconcile no-show charges with Payments / night audit.',
        'After release, offer the room to waitlisted or walk-in demand.',
        'Review repeated no-show guests with management for future guarantee rules.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Marking no-show too early.',
        'Forgetting to release inventory.',
        'Ignoring late-arrival notes on the booking.',
        'Charging no-show against policy or without authorisation.',
        'Marking no-show on the wrong booking with a similar name.',
        'Creating a walk-in for the same guest without clearing the old hold.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Room still blocked',
            'Status not saved or channel lag',
            'Confirm status saved; check inventory/channel sync',
          ],
          [
            'Guest arrives after no-show',
            'Late arrival after cut-off',
            'Escalate supervisor; reinstate or new booking per policy; document',
          ],
          [
            'Charge not posting',
            'Permission or rate rule',
            'Use authorised payment/charge flow; check policy',
          ],
          [
            'OTA conflict',
            'Channel status differs',
            'Coordinate with Distribution owner before forcing PMS-only changes',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'No show vs cancel?',
          a: 'Cancel is usually before arrival by request. No show is failure to arrive without timely cancellation.',
        },
        {
          q: 'Can I still sell the room after no-show?',
          a: 'Yes when inventory is released and policy allows. Walk-in demand often fills no-show rooms late at night.',
        },
        {
          q: 'What if advance was paid?',
          a: 'Follow refund/forfeit policy with Payments and supervisor approval. Note the decision on the booking.',
        },
        {
          q: 'Should I call every pending arrival?',
          a: 'Follow property SOP. Calling guaranteed or VIP arrivals before cut-off is best practice.',
        },
        {
          q: 'Does no-show affect reports?',
          a: 'Yes — booking and occupancy reports distinguish no-shows from cancellations and checked-out stays.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'booking-list',
    title: 'Booking List & Management',
    moduleId: 'booking',
    summary:
      'Search, filter, edit, cancel, and export bookings from one list.',
    keywords: [
      'booking list',
      'search booking',
      'cancel',
      'edit booking',
      'booking code',
    ],
    pathHint: 'Booking → Booking List',
    related: ['booking-detail', 'edit-booking', 'reservation'],
    overview: [
      ...paragraphs(
        'Booking List is the master directory of stays. Use search and filters to find any booking by guest, phone, code, status, or dates — this is the first screen to open when the phone rings.',
        'From each row you open Booking Detail for folio work, then Edit Booking, cancel, check-in, or check-out as status allows. Prefer editing an existing booking over creating a duplicate.',
        'Example: Caller says “Booking under Priya, mobile ending 7821.” Search mobile → open detail → confirm dates → make the change → save. Total time should be under a minute once you trust search.',
        'Filters prevent overwhelm on busy properties: today’s arrivals, in-house only, or cancelled for audit. Clear filters if results look empty.',
        'Export (when available) helps supervisors share arrival lists or audit samples — still treat guest data as confidential.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Confirm correct hotel context in the header',
        'Ask callers for mobile used at booking or booking code',
        'Know whether you need arrivals, in-house, or historical range',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Booking List.' },
        {
          text: 'Search by guest name, mobile, or booking code. Try partial mobile if needed.',
        },
        {
          text: 'Apply filters (status, date range, room type) to narrow results.',
        },
        { text: 'Open a row to view Booking Detail.' },
        {
          text: 'From detail, edit, cancel, add charges, or continue to check-in/out as needed.',
        },
        {
          text: 'When cancelling, add a clear reason note for audit and refund handling.',
        },
        {
          text: 'If search returns nothing, clear filters, check spelling, and verify hotel — then only create a new reservation if truly missing.',
        },
        {
          text: 'Use export if available and authorised for the operational list you need.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Search box', '—', 'Finds by name, mobile, code'],
        ['Filters', '—', 'Status, dates, room type, etc.'],
        ['Results table', '—', 'Matching bookings'],
        ['Row open', '—', 'Goes to Booking Detail'],
        ['Export', 'If available', 'Downloads list for ops/audit'],
      ]),
      bullets(
        ['Search box', 'Filters', 'Results table', 'Row actions', 'Export if available'],
        'List layout'
      ),
    ],
    workflow: [
      workflow('Find and fix', [
        'Search list',
        'Open detail',
        'Edit or process',
        'Confirm calendar update',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'List data reflects live booking records for the hotel context. Cancelling reopens availability subject to rules; edits that change dates re-validate inventory.',
        'Status filters change which lifecycle stage you see — empty results are often over-filtered, not missing data.',
        'Duplicate bookings usually appear as two rows with the same mobile and overlapping dates — cancel the accidental one after confirming which is correct.',
        'Audit/history for sensitive actions is available from booking detail/audit where enabled for your role.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Phone search',
        'Ask callers for the mobile used at booking — fastest match. Name-only search is slower and error-prone.'
      ),
      callout(
        'tip',
        'Clear filters',
        'If you “cannot find” a known booking, clear status and date filters first.'
      ),
      callout(
        'note',
        'Code on confirmation',
        'Train guests to save the booking code from SMS/WhatsApp — it is the cleanest lookup.'
      ),
    ],
    bestPractices: [
      bullets([
        'Prefer edit over duplicate create.',
        'Cancel with a reason note.',
        'Use status filters during shift briefings (arrivals / in-house / departures).',
        'Protect guest PII when exporting lists.',
        'When two results look alike, open both details before changing either.',
        'After cancel, verify the calendar slot freed if you plan to resell.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Editing the wrong booking with similar names.',
        'Creating a new reservation because filters hid the existing one.',
        'Cancelling without notes — disputes become he-said-she-said.',
        'Searching in the wrong hotel after a property switch.',
        'Exporting and sharing guest lists on public chats.',
        'Assuming “no results” means the OTA booking never synced — widen search first.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No results',
            'Filters too tight / wrong hotel / typo',
            'Clear filters; try booking code; check hotel context',
          ],
          [
            'Too many results',
            'Common name',
            'Add mobile or date filter',
          ],
          [
            'Cannot open detail',
            'Permission or transient error',
            'Refresh; confirm manage_booking_operations',
          ],
          [
            'Cancel blocked',
            'Status/payment rules',
            'Follow policy; ask supervisor for financial cases',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where is booking audit?',
          a: 'Booking-related audit/history is available via booking detail/audit actions where enabled for your role.',
        },
        {
          q: 'Can I cancel from the list?',
          a: 'Typically you open Booking Detail and cancel from actions, depending on your UI permissions.',
        },
        {
          q: 'How do I find in-house guests only?',
          a: 'Filter by Checked In status (and current dates as needed).',
        },
        {
          q: 'What if the guest gives an OTA number?',
          a: 'Search that confirmation text in notes/code fields or by mobile; Distribution sync stores the PMS booking alongside channel references when available.',
        },
        {
          q: 'Is Booking List the same as Stay calendar?',
          a: 'No. List is searchable rows; Stay/Booking calendar is visual. Both read the same bookings.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'booking-detail',
    title: 'Booking Detail',
    moduleId: 'booking',
    summary:
      'Single-booking command centre: guest, rooms, charges, payments, documents, and actions.',
    keywords: ['booking detail', 'folio', 'booking page', 'extra charges'],
    pathHint: 'Booking List → Booking Detail',
    related: ['edit-booking', 'check-in', 'check-out', 'payments'],
    overview: [
      ...paragraphs(
        'Booking Detail shows everything about one stay. Staff use it to verify information, post extra charges, send payment/confirmation links, print documents, and launch related actions like edit, check-in, or check-out.',
        'When a guest is at the desk asking “what is my balance?” or “can you add a late checkout?”, open Booking Detail — not a new reservation.',
        'The folio section combines room charges, extras, and linked payments. POS restaurant charges that were room-charged should be considered before you quote a checkout total.',
        'Example: In-house guest wants an extra bed. Open Booking Detail → add the extra charge per policy → note “extra bed approved by DM” → confirm the new balance with the guest.',
        'Action menus vary by status and role. A checked-out booking shows fewer operational actions; a reserved booking emphasises allocation and check-in.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Open from Booking List, calendar, arrivals, or search',
        'Confirm you have the correct booking code before posting charges',
        'Know which actions your role allows (edit, refund, rate override)',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open a booking from Booking List, calendar, or arrivals.',
        },
        {
          text: 'Review guest, dates, room, status, and notes at the top summary.',
        },
        {
          text: 'Check documents and contact fields; update via Edit if something is wrong.',
        },
        {
          text: 'Review charges / folio and payments already captured.',
        },
        {
          text: 'Use actions to edit, add charges, record payment, print, or send confirmation/payment links when enabled.',
        },
        {
          text: 'Add a clear note after every exception (rate override, late CI, complaint).',
        },
        {
          text: 'Proceed to check-in/out from actions when status allows, or return to the list.',
        },
        {
          text: 'If investigating a dispute, open audit/history actions where available for your role.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Guest & stay summary',
          'Room assignment',
          'Charges / folio',
          'Payments',
          'Documents',
          'Action menus (edit, PDFs, links, audit)',
        ],
        'Detail sections'
      ),
      fieldTable([
        ['Status', '—', 'Drives which actions are allowed'],
        ['Room number', '—', 'Allocated / in-house room'],
        ['Folio / charges', '—', 'What the guest owes'],
        ['Payments', '—', 'What has been collected'],
        ['Notes', 'Recommended', 'Shift-to-shift operational memory'],
        ['Documents', 'Policy', 'ID and booking files'],
      ]),
    ],
    workflow: [
      workflow('Desk inquiry', [
        'Search',
        'Open detail',
        'Answer guest',
        'Update notes/charges',
        'Take payment if needed',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Extra charges increase folio balance. Payments reduce balance. Status transitions remain governed by check-in/out and cancellation rules — detail is where you see the result.',
        'Payment links (when communications/payments are enabled) let guests pay advances without reading card data aloud at the desk.',
        'Documents stored on the booking support compliance and front-office verification; keep them readable and current.',
        'Posting charges to the wrong booking is a serious error — always match room number and booking code before save.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Payment links',
        'When enabled, send payment links for advances instead of taking card data verbally.'
      ),
      callout(
        'tip',
        'Balance questions',
        'Refresh folio after POS settlement before quoting checkout totals.',
      ),
      callout(
        'note',
        'Notes are for the next shift',
        'Write like the night auditor will read it: time, who approved, what was promised.'
      ),
    ],
    bestPractices: [
      bullets([
        'Leave a clear note after every exception (rate override, late CI).',
        'Verify room number on detail before posting mini-bar or paid extras.',
        'Use print/PDF confirmation when guests ask for written proof.',
        'Keep ID documents complete before police-log reporting days.',
        'For corporate guests, store GST/billing instructions in notes early.',
        'Open audit when voids or unusual discounts appear.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Posting charges to the wrong booking.',
        'Quoting balance without including POS room charges.',
        'Editing dates from memory instead of using Edit Booking availability checks.',
        'Leaving vague notes (“VIP”) without actionable detail.',
        'Sharing payment link screenshots that expose other guests’ data.',
        'Assuming missing actions mean a bug — often status or permission.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Actions missing',
            'Role permissions or booking status',
            'Check role permissions and booking status',
          ],
          [
            'Balance looks wrong',
            'Unsettled POS or missing payment',
            'Review folio lines + POS Orders + Payments',
          ],
          [
            'Cannot add charge',
            'Permission or checked-out lock',
            'Ask supervisor; use authorised finance correction',
          ],
          [
            'PDF/print fails',
            'Browser pop-up / setup',
            'Allow pop-ups; retry; use Draft Invoice tools if configured',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I print confirmation here?',
          a: 'Yes where confirmation/PDF actions are available on the booking.',
        },
        {
          q: 'How do I change dates?',
          a: 'Use Edit Booking from this page — do not create a second booking.',
        },
        {
          q: 'Where do I take a payment?',
          a: 'Use payment actions on the booking / Payments flows linked from detail, per your finance setup.',
        },
        {
          q: 'Can guests see this screen?',
          a: 'No — Booking Detail is staff-only. Share confirmations/receipts via official print or message actions.',
        },
        {
          q: 'What if two people share one room?',
          a: 'Keep one primary folio booking; record companion details in notes/documents per compliance SOP.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'edit-booking',
    title: 'Edit Booking',
    moduleId: 'booking',
    summary:
      'Change stay details safely — dates, guest info, rooms, and preferences.',
    keywords: [
      'edit booking',
      'amend',
      'change dates',
      'modify reservation',
    ],
    pathHint: 'Booking Detail → Edit',
    related: ['booking-detail', 'room-allocation', 'reservation'],
    overview: [
      ...paragraphs(
        'Edit Booking amends an existing record. Use it for date changes, guest corrections, room-type changes, and preference updates instead of creating a second booking.',
        'Date changes re-check availability. Extending a stay can fail if the room or type is already sold for those nights — offer alternatives rather than forcing overlaps.',
        'Example: Guest calls to leave one day early. Open Booking Detail → Edit Booking → shorten departure date → review rate impact → save → confirm any refund/adjustment with supervisor and Payments.',
        'In-house edits (especially room moves) need housekeeping coordination. Changing contact details is simpler but still matters for WhatsApp and payment links.',
        'Checked-out bookings are usually limited to edit. Financial corrections may need finance permissions — ask a supervisor rather than inventing a new stay.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      callout(
        'warning',
        'Date changes',
        'Changing dates re-checks availability. Extensions can fail if the room is already sold.'
      ),
      bullets([
        'Open the correct booking from Booking List first',
        'Note old dates/rates before major changes for dispute handling',
        'For extensions, check Dashboard/HK for continuing availability',
        'Know who must approve rate differences',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Booking Detail → Edit Booking.' },
        {
          text: 'Update fields carefully (dates, guest, room type, notes). Read validation messages.',
        },
        {
          text: 'If changing dates, review availability and any rate impact shown.',
        },
        {
          text: 'If changing room type or room number needs, plan Room Allocation / HK readiness.',
        },
        { text: 'Save and confirm the calendar reflects the change.' },
        {
          text: 'Inform the guest of any price difference and collect or refund via Payments as authorised.',
        },
        {
          text: 'Add a note: what changed, who requested, who approved.',
        },
        {
          text: 'For in-house room moves, notify housekeeping and confirm keys match the new room.',
        },
      ]),
    ],
    screens: [
      bullets(
        ['Editable booking form', 'Save / cancel', 'Validation messages'],
        'Edit screen areas'
      ),
      fieldTable([
        ['Stay dates', 'When changing', 'Re-validates inventory'],
        ['Guest fields', 'As needed', 'Corrections to identity/contact'],
        ['Room type', 'As needed', 'May require re-allocation'],
        ['Notes / requests', 'No', 'Preferences and ops instructions'],
        ['Save', 'Yes', 'Commits amendment if validations pass'],
      ]),
    ],
    workflow: [
      workflow('Amendment', [
        'Request change',
        'Check availability',
        'Edit & save',
        'Adjust charges/payments',
        'Confirm with guest',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Inventory is released for removed nights and reserved for new nights when save succeeds. Failed availability checks block the save to prevent oversell.',
        'Shortening a stay may leave posted charges that need authorised adjustment — editing dates alone does not always auto-fix every folio line.',
        'Guest field edits update the booking record used for communication and compliance; keep them aligned with ID documents when required.',
        'Creating a duplicate reservation to “work around” a failed edit causes double inventory holds — resolve the availability conflict instead.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'In-house edits',
        'Room moves for in-house guests need HK coordination and guest agreement.'
      ),
      callout(
        'tip',
        'Screenshot old dates',
        'For large amendments, note previous dates/rates in the booking note before save.'
      ),
      callout(
        'note',
        'Same-day extend',
        'Before extending tonight’s departure to tomorrow, confirm the room is not allocated to a new arrival.'
      ),
    ],
    bestPractices: [
      bullets([
        'Screenshot or note old dates before major changes for dispute handling.',
        'Tell the guest the new total before saving irreversible expectations.',
        'Align Meal plan / rate plan changes with what F&B will serve.',
        'After edit, reopen Booking Detail to verify folio still makes sense.',
        'Use supervisor approval for discounts applied during amendment.',
        'Keep OTA amendments consistent with channel rules when the booking originated online.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Shortening stay without adjusting charges already posted.',
        'Creating a second booking because edit failed availability.',
        'Changing room type without reallocating a suitable physical room.',
        'Silent rate changes the guest only discovers at checkout.',
        'Editing the wrong booking from search results.',
        'Forcing extensions into OOO rooms.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Save blocked',
            'Availability conflict',
            'Resolve conflict or choose another room/type/dates',
          ],
          [
            'Rate looks wrong after edit',
            'Plan/date recalculation',
            'Review folio; escalate override to supervisor',
          ],
          [
            'Guest already arrived',
            'In-house constraints',
            'Use allowed in-house edit + allocation; avoid cancel/recreate',
          ],
          [
            'Cannot edit checked-out',
            'Status lock',
            'Ask supervisor for finance/correction flow',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I edit a checked-out booking?',
          a: 'Usually limited; ask a supervisor — financial corrections may need finance permissions.',
        },
        {
          q: 'Why did extension fail?',
          a: 'Those nights are likely sold, blocked, or the physical room is unavailable. Try another room/type or dates.',
        },
        {
          q: 'Should I cancel and rebook instead?',
          a: 'Almost never. Cancel/rebook breaks history, payments, and channel links. Fix with Edit Booking.',
        },
        {
          q: 'Can I change only the mobile number?',
          a: 'Yes — edit guest contact fields and save. Accurate mobiles keep search and messages working.',
        },
        {
          q: 'Do date changes notify the guest automatically?',
          a: 'Only if your communications setup sends updates. Always confirm verbally/WhatsApp per SOP.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stay-view',
    title: 'Stay View & Booking Calendar',
    moduleId: 'booking',
    summary:
      'Work with stay-centric and booking-date calendar views for in-house and reservations.',
    keywords: [
      'stay',
      'booking calendar',
      'booking page',
      'in-house view',
    ],
    pathHint: 'Booking → Stay / Booking calendar',
    related: ['dashboard-home', 'booking-list', 'check-in'],
    overview: [
      ...paragraphs(
        'Stay and Booking calendar pages visualise reservations across time. Use them to understand occupancy, spot gaps for walk-ins, plan allocations, and jump into bookings without hunting the list.',
        'Dashboard Quick Book is fastest for creating a booking from a calendar cell; Stay/Booking pages are stronger for overview and planning across multiple days.',
        'Example evening use: open tomorrow’s view, count arrivals, note tight room-type sell-outs, brief HK on early cleans, and pre-allocate VIP rooms before night shift handover.',
        'Booking-date and stay-centric modes emphasise different operational questions (when booked vs how stays occupy rooms), but both read the same booking records — changes still happen through booking actions.',
        'Be careful around midnight and date boundaries: a departure day morning is still occupied until checkout completes.'
      ),
    ],
    beforeYouBegin: [
      bookingPerm,
      bullets([
        'Confirm hotel context',
        'Know whether you need today ops or multi-day planning',
        'Use Booking List when you need exact mobile/code search',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Stay or Booking calendar from booking navigation.',
        },
        {
          text: 'Navigate dates and scan occupancy blocks by room or type as shown.',
        },
        {
          text: 'Click a booking block to open details or actions.',
        },
        {
          text: 'Identify gaps suitable for walk-ins or upgrades.',
        },
        {
          text: 'Use the view to plan allocations and departures; switch to Room Allocation / Check-in to execute.',
        },
        {
          text: 'For fastest create from a free slot, use Dashboard Quick Book when that fits your SOP.',
        },
        {
          text: 'Recheck the view after edits to ensure blocks moved correctly.',
        },
        {
          text: 'Brief HK/F&B on peak arrival clusters you see for the next day.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Calendar / list modes',
          'Booking blocks',
          'Date navigation',
          'Click-through to Booking Detail',
        ],
        'Calendar areas'
      ),
      fieldTable([
        ['Date navigation', '—', 'Moves the visible range'],
        ['Booking block', '—', 'One stay’s visual occupancy'],
        ['Filters / modes', '—', 'Stay-centric vs booking-date emphasis'],
        ['Open booking', '—', 'Jumps to detail/actions'],
      ]),
    ],
    workflow: [
      workflow('Ops planning', [
        'Open calendar',
        'Identify peaks',
        'Pre-allocate',
        'Brief HK/F&B',
        'Process arrivals next day',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Views are presentations of booking data; creating, editing, checking in, and checking out still happen through booking actions.',
        'Occupancy blocks reflect reserved and in-house stays. Cancelled/no-show bookings should not continue to block sellable space once processed.',
        'Dashboard and Stay/Booking calendars can both show overlapping operational truth — trust Booking Detail if two views look momentarily out of sync, then refresh.',
        'Filters on the view do not delete bookings; they only hide them from the current screen.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Dashboard vs Stay',
        'Dashboard Quick Book is fastest for create; Stay/Booking pages are strong for overview and handover planning.'
      ),
      callout(
        'tip',
        'Tomorrow briefing',
        'Every evening, scan tomorrow’s arrivals and departures from this view before shift handover.'
      ),
      callout(
        'tip',
        'Walk-in gaps',
        'Visible empty nights on popular types are your walk-in opportunity — confirm HK Clean rooms before promising.'
      ),
    ],
    bestPractices: [
      bullets([
        'Check tomorrow’s arrivals every evening from this view.',
        'Use calendar peaks to schedule housekeeping overtime early.',
        'Click through before telling a guest “that room is free.”',
        'Combine with Booking List search when the guest is on the phone.',
        'Avoid reading midnight edge stays too quickly — confirm checkout status.',
        'Keep one screen on calendar and one on HK board during busy arrivals.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Misreading timezone/date boundaries around midnight shifts.',
        'Assuming a coloured block is checked-in when it is only reserved.',
        'Promising a gap without checking HK cleanliness.',
        'Making changes on the wrong day column.',
        'Ignoring filters that hide arrivals you are looking for.',
        'Using calendar alone for mobile search — use Booking List.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Missing bookings',
            'Filters or wrong hotel',
            'Verify filters and hotel context; search Booking List',
          ],
          [
            'Block dates look wrong',
            'Recent edit not refreshed',
            'Refresh; open Booking Detail to confirm stored dates',
          ],
          [
            'Cannot open booking',
            'Permission / UI glitch',
            'Retry; open via Booking List with code',
          ],
          [
            'Occupancy looks oversold',
            'Overlapping types or blocks',
            'Inspect Booking Detail rows; check inventory blocks',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Why two calendar entry points?',
          a: 'Booking-date and stay-centric modes emphasise different operational questions; both read the same bookings.',
        },
        {
          q: 'Can I drag to create bookings here?',
          a: 'Creation patterns vary by screen; Dashboard Quick Book is the common fast-create path. Follow what your deployment shows.',
        },
        {
          q: 'Is this a replacement for Check-in?',
          a: 'No. Use it to find and plan; process arrivals on Check-in.',
        },
        {
          q: 'Does the calendar show POS orders?',
          a: 'No — it visualises stays. Open POS Orders / Booking Detail for F&B charges.',
        },
        {
          q: 'What if the calendar disagrees with Dashboard?',
          a: 'Refresh both, confirm hotel context, then trust Booking Detail for the specific stay.',
        },
      ]),
    ],
  }),
];
